import React from "react";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-white via-gray-50 to-blue-50 text-gray-900">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-md border-b border-gray-100 py-5 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 flex justify-between items-center">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <img
              src="/logo.png"
              alt="Americar Logo"
              className="w-10 h-10 object-contain"
            />
            <span className="text-2xl font-semibold font-poppins text-blue-700">
              Americar Privacy Policy
            </span>
          </div>

          {/* Back to Home */}
          <a
            href="/"
            className="text-blue-600 hover:text-blue-800 font-medium transition-all"
          >
            ← Back to Home
          </a>
        </div>
      </header>

      {/* Content */}
      <main className="flex-grow max-w-4xl mx-auto px-6 py-16 sm:py-24">
        <h1 className="text-3xl sm:text-4xl font-bold font-poppins text-gray-900 mb-6 text-center">
          Americar Privacy Policy
        </h1>

        <p className="text-gray-600 mb-8 text-center">
          <strong>Last updated:</strong> [Insert date before publishing]
        </p>

        <div className="space-y-8 text-gray-700 leading-relaxed">
          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              1. Introduction
            </h2>
            <p>
              Americar (“we,” “us,” or “our”) is a mobile application available
              on the Apple App Store and Google Play Store. We are committed to
              protecting your privacy and maintaining transparency about how we
              collect, use, and share information. This Privacy Policy explains
              how we handle personal information of users (“you,” “your,” or
              “Driver”) who use the Americar app in the United States. By using
              Americar, you agree to the practices described herein.
            </p>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              2. Eligibility and Scope
            </h2>
            <p>
              You must be at least 18 years old and reside in the United States
              to use Americar. The app is not intended for minors. If we learn
              that information from a person under 18 has been collected, we
              delete it promptly. This policy incorporates all rights and
              disclosures required under the California Consumer Privacy Act
              (CCPA) and California Privacy Rights Act (CPRA).
            </p>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              3. Information We Collect
            </h2>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Account and Identity Data:</strong> Name, email,
                password (hashed only), phone number, vehicle details, and
                unique Americar QR identifier.
              </li>
              <li>
                <strong>Device Permissions:</strong> Camera, Photos/Media/Files,
                Location (precise and background), Contacts, Push notifications,
                Device ID.
              </li>
              <li>
                <strong>Analytics:</strong> Firebase Analytics and Crashlytics
                logs.
              </li>
              <li>
                <strong>Payments:</strong> Processed via Stripe; limited
                metadata only (card brand, last four digits, billing ZIP).
              </li>
            </ul>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              4. How We Use Your Information
            </h2>
            <p>
              We use your data to manage your account, locate vendors, facilitate
              roadside assistance, send notifications, deliver in-app vendor
              advertisements, personalize experiences, analyze performance, and
              comply with laws.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              5. Sharing and Disclosure
            </h2>
            <p>
              Service providers (Firebase, Stripe, cloud hosting), vendors (to
              perform services), legal obligations, and aggregated analytics.
              Americar does not sell personal information.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              6. Unique QR Code System
            </h2>
            <p>
              Each driver has a unique, non-shareable QR code. Only authorized
              Americar vendor devices can scan it.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              7. Location Data Retention
            </h2>
            <p>
              Collected continuously for vendor search and roadside assistance.
              Retained one year, then deleted or anonymized.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              8. Data Retention Schedule
            </h2>
            <p>
              Account data: until termination; logs and analytics: one year;
              backups deleted within cycle. Users may request deletion in-app or
              via email.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              9. Security
            </h2>
            <p>
              TLS encryption, Firebase encryption at rest, access controls. No
              system is fully secure.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              10. California Privacy Rights (CCPA/CPRA)
            </h2>
            <p>
              California residents may know, delete, correct, or opt out of data
              sharing. No sale of data. Contact:{" "}
              <a
                href="mailto:vroom@americar.app"
                className="text-blue-600 hover:underline"
              >
                vroom@americar.app
              </a>
              .
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              11. Children’s Privacy
            </h2>
            <p>
              Not for users under 18. We do not intentionally collect data from
              minors.
            </p>
          </section>

          {/* 12 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              12. International Data Processing
            </h2>
            <p>
              Data may be processed on servers outside the U.S. (Firebase, cloud
              providers). Safeguards applied.
            </p>
          </section>

          {/* 13 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              13. Changes to This Policy
            </h2>
            <p>
              We may update this policy periodically; changes will be
              communicated via the app.
            </p>
          </section>

          {/* 14 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              14. Contact Us
            </h2>
            <p>
              Email:{" "}
              <a
                href="mailto:vroom@americar.app"
                className="text-blue-600 hover:underline"
              >
                vroom@americar.app
              </a>
            </p>
            <p>Mailing Address: [Optional – add if available]</p>
          </section>

          {/* 15 */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              15. Store Disclosures
            </h2>
            <p className="font-semibold mt-3 mb-1">Apple App Store</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Collected Data:</strong> Identifiers, Location, Contact
                Info, Contacts, User Content, Usage Data, Diagnostics, Financial
                Info (Stripe).
              </li>
              <li>
                <strong>Purpose:</strong> Account management, vendor matching,
                analytics, notifications.
              </li>
              <li>
                <strong>Shared with:</strong> Firebase, Stripe.
              </li>
            </ul>

            <p className="font-semibold mt-4 mb-1">Google Play Store</p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <strong>Data Collected:</strong> Identifiers, Location, Contact
                Info, Contacts, User Content, Usage Data, Diagnostics, limited
                Stripe data.
              </li>
              <li>
                All data encrypted in transit and at rest. User deletion available
                via app or{" "}
                <a
                  href="mailto:vroom@americar.app"
                  className="text-blue-600 hover:underline"
                >
                  vroom@americar.app
                </a>
                .
              </li>
              <li>No third-party ad SDKs or sale of data.</li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-400 py-8 text-center">
        <p className="text-sm">
          © 2025 Americar. All rights reserved. | Privacy Policy
        </p>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
